import React, { useMemo } from 'react';
import { geoAlbersUsa, geoPath } from 'd3-geo';
import { feature, merge } from 'topojson-client';
import us from 'us-atlas/states-10m.json';

export default function UsaNetworkMap({ className = '' }) {
  const { mapPath, nodes, links } = useMemo(() => {
    const states = feature(us, us.objects.states);
    const lower48 = {
      type: 'FeatureCollection',
      features: states.features.filter(
        (state) => !['02', '15', '60', '66', '69', '72', '78'].includes(String(state.id).padStart(2, '0'))
      ),
    };

    const lower48Ids = new Set(lower48.features.map((state) => state.id));
    const lower48Outline = merge(
      us,
      us.objects.states.geometries.filter((geometry) => lower48Ids.has(geometry.id))
    );

    const projection = geoAlbersUsa().fitSize([1120, 560], lower48Outline);
    const pathGenerator = geoPath(projection);
    const mapPath = pathGenerator(lower48Outline);
    const locations = {
      Seattle: [-122.3321, 47.6062],
      Spokane: [-117.426, 47.6588],
      Portland: [-122.6765, 45.5152],
      Boise: [-116.2023, 43.615],
      SanFrancisco: [-122.4194, 37.7749],
      Sacramento: [-121.4944, 38.5816],
      Fresno: [-119.7871, 36.7378],
      LosAngeles: [-118.2437, 34.0522],
      SanDiego: [-117.1611, 32.7157],
      LasVegas: [-115.1398, 36.1699],
      Phoenix: [-112.074, 33.4484],
      Tucson: [-110.9747, 32.2226],
      Albuquerque: [-106.6504, 35.0844],
      SaltLake: [-111.891, 40.7608],
      Denver: [-104.9903, 39.7392],
      ColoradoSprings: [-104.8214, 38.8339],
      ElPaso: [-106.485, 31.7619],
      SanAntonio: [-98.4936, 29.4241],
      Austin: [-97.7431, 30.2672],
      Dallas: [-96.797, 32.7767],
      Houston: [-95.3698, 29.7604],
      Tulsa: [-95.9928, 36.154],
      OklahomaCity: [-97.5164, 35.4676],
      KansasCity: [-94.5786, 39.0997],
      Omaha: [-95.9345, 41.2565],
      DesMoines: [-93.6091, 41.5868],
      Minneapolis: [-93.265, 44.9778],
      Milwaukee: [-87.9065, 43.0389],
      Chicago: [-87.6298, 41.8781],
      StLouis: [-90.1994, 38.627],
      Memphis: [-90.049, 35.1495],
      Indianapolis: [-86.1581, 39.7684],
      Cincinnati: [-84.512, 39.1031],
      Nashville: [-86.7816, 36.1627],
      Louisville: [-85.7585, 38.2527],
      Atlanta: [-84.388, 33.749],
      Birmingham: [-86.8025, 33.5186],
      Jacksonville: [-81.6557, 30.3322],
      Tampa: [-82.4572, 27.9506],
      Miami: [-80.1918, 25.7617],
      Charleston: [-79.9311, 32.7765],
      Charlotte: [-80.8431, 35.2271],
      Raleigh: [-78.6382, 35.7796],
      Richmond: [-77.436, 37.5407],
      Washington: [-77.0369, 38.9072],
      Baltimore: [-76.6122, 39.2904],
      Philadelphia: [-75.1652, 39.9526],
      NewYork: [-74.006, 40.7128],
      Boston: [-71.0589, 42.3601],
      Hartford: [-72.6851, 41.7637],
      Pittsburgh: [-79.9959, 40.4406],
      Buffalo: [-78.8784, 42.8864],
      Detroit: [-83.0458, 42.3314],
      Cleveland: [-81.6944, 41.4993],
      NewOrleans: [-90.0715, 29.9511],
    };

    const projectedNodes = Object.entries(locations)
      .map(([name, coords]) => {
        const point = projection(coords);
        if (!point) return null;
        return { name, x: point[0], y: point[1] };
      })
      .filter(Boolean);

    const byName = Object.fromEntries(projectedNodes.map((node) => [node.name, node]));
    const linkNames = [
      ['Seattle', 'Portland'],
      ['Seattle', 'Spokane'],
      ['Spokane', 'Boise'],
      ['Boise', 'SaltLake'],
      ['Portland', 'SanFrancisco'],
      ['Portland', 'Boise'],
      ['SanFrancisco', 'LosAngeles'],
      ['SanFrancisco', 'Sacramento'],
      ['Sacramento', 'Fresno'],
      ['Fresno', 'LosAngeles'],
      ['LosAngeles', 'SanDiego'],
      ['LosAngeles', 'LasVegas'],
      ['LasVegas', 'Phoenix'],
      ['Phoenix', 'Tucson'],
      ['Tucson', 'ElPaso'],
      ['Phoenix', 'Albuquerque'],
      ['Albuquerque', 'Denver'],
      ['Albuquerque', 'OklahomaCity'],
      ['Phoenix', 'Denver'],
      ['SaltLake', 'Denver'],
      ['Seattle', 'SaltLake'],
      ['Denver', 'ColoradoSprings'],
      ['ColoradoSprings', 'Albuquerque'],
      ['Denver', 'KansasCity'],
      ['Denver', 'Dallas'],
      ['KansasCity', 'StLouis'],
      ['KansasCity', 'Tulsa'],
      ['Tulsa', 'OklahomaCity'],
      ['OklahomaCity', 'Dallas'],
      ['StLouis', 'Chicago'],
      ['StLouis', 'Indianapolis'],
      ['Indianapolis', 'Cincinnati'],
      ['Cincinnati', 'Cleveland'],
      ['Chicago', 'Detroit'],
      ['Chicago', 'Milwaukee'],
      ['Detroit', 'Cleveland'],
      ['Cleveland', 'Pittsburgh'],
      ['Pittsburgh', 'Philadelphia'],
      ['Pittsburgh', 'Buffalo'],
      ['Buffalo', 'Boston'],
      ['Cleveland', 'NewYork'],
      ['NewYork', 'Boston'],
      ['Boston', 'Hartford'],
      ['Hartford', 'NewYork'],
      ['NewYork', 'Philadelphia'],
      ['Philadelphia', 'Washington'],
      ['Washington', 'Baltimore'],
      ['Washington', 'Richmond'],
      ['Washington', 'Raleigh'],
      ['Raleigh', 'Charlotte'],
      ['Richmond', 'Raleigh'],
      ['Charlotte', 'Atlanta'],
      ['Charlotte', 'Charleston'],
      ['Charleston', 'Jacksonville'],
      ['Atlanta', 'Miami'],
      ['Atlanta', 'Birmingham'],
      ['Birmingham', 'NewOrleans'],
      ['Jacksonville', 'Tampa'],
      ['Tampa', 'Miami'],
      ['Dallas', 'Houston'],
      ['Dallas', 'Austin'],
      ['Austin', 'SanAntonio'],
      ['SanAntonio', 'Houston'],
      ['ElPaso', 'SanAntonio'],
      ['Houston', 'NewOrleans'],
      ['NewOrleans', 'Atlanta'],
      ['Dallas', 'KansasCity'],
      ['Dallas', 'Nashville'],
      ['Nashville', 'Charlotte'],
      ['Nashville', 'Louisville'],
      ['Louisville', 'Cincinnati'],
      ['Memphis', 'Nashville'],
      ['Memphis', 'StLouis'],
      ['Memphis', 'Dallas'],
      ['Minneapolis', 'Chicago'],
      ['Omaha', 'Minneapolis'],
      ['Omaha', 'DesMoines'],
      ['DesMoines', 'Chicago'],
      ['Denver', 'Omaha'],
      ['KansasCity', 'Omaha'],
      ['Atlanta', 'Washington'],
      ['Chicago', 'Washington'],
      ['LosAngeles', 'Dallas'],
      ['Denver', 'Chicago'],
    ];

    const links = linkNames
      .map(([sourceName, targetName]) => {
        if (!byName[sourceName] || !byName[targetName]) return null;
        return { source: byName[sourceName], target: byName[targetName] };
      })
      .filter(Boolean);

    return { mapPath, nodes: projectedNodes, links };
  }, []);

  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 640" role="img" aria-label="USA network map">
      <defs>
        <filter id="usaNodeGlow" x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur stdDeviation="5" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id="usaMapGlow" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="2.8" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <linearGradient id="usaStroke" x1="0" x2="1">
          <stop offset="0%" stopColor="#006fff" stopOpacity="0.35" />
          <stop offset="48%" stopColor="#1ed9ff" stopOpacity="0.82" />
          <stop offset="100%" stopColor="#006fff" stopOpacity="0.4" />
        </linearGradient>
        <radialGradient id="usaNodeGradient">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="38%" stopColor="#7befff" />
          <stop offset="100%" stopColor="#007bff" />
        </radialGradient>
        <clipPath id="usaClip">
          <path d={mapPath} />
        </clipPath>
      </defs>

      <path
        d={mapPath}
        fill="rgba(0, 95, 255, 0.018)"
        stroke="url(#usaStroke)"
        strokeWidth="1.7"
        strokeLinejoin="round"
        strokeOpacity="0.68"
        filter="url(#usaMapGlow)"
      />

      <g clipPath="url(#usaClip)">
        <g fill="none" stroke="#168cff" strokeWidth="0.95" strokeLinecap="round" strokeOpacity="0.5">
          {links.map((link, index) => (
            <line key={index} x1={link.source.x} y1={link.source.y} x2={link.target.x} y2={link.target.y} />
          ))}
        </g>
        <g fill="none" stroke="#00c8ff" strokeWidth="0.55" strokeLinecap="round" strokeOpacity="0.22">
          {[...links.slice(5, 22), ...links.slice(24, 35)].map((link, index) => (
            <line key={`faint-${index}`} x1={link.source.x} y1={link.source.y} x2={link.target.x} y2={link.target.y} />
          ))}
        </g>
      </g>

      <g filter="url(#usaNodeGlow)">
        {nodes.map((node, index) => (
          <circle
            key={node.name}
            cx={node.x}
            cy={node.y}
            r={index % 4 === 0 ? 4.8 : index % 3 === 0 ? 4 : 3.2}
            fill="url(#usaNodeGradient)"
            opacity="0.95"
          />
        ))}
        {nodes
          .filter((_, index) => index % 5 === 0)
          .map((node) => (
            <circle key={`center-${node.name}`} cx={node.x} cy={node.y} r="1.7" fill="#ffffff" opacity="0.95" />
          ))}
      </g>
    </svg>
  );
}
